import React, { useState } from 'react'
import { Title, Description } from './ProfileModal.styles'
import SelectableDocumentList from '../../../designSystems/components/SelectableDocumentList'
import { useGetSharesQuery, WorkspaceMinimalFragment } from '@sketch/gql-types'
import { useDebounceValue } from '@sketch/utils'
import { handleFetchMore } from '@sketch/components'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  ContentContainer,
  Separator,
  Wrapper,
  IconWrapper,
  StyledFolderClosed,
} from './SelectDocumentsStep.styles'
import { ReactComponent as SearchIcon } from '@sketch/icons/search-64'
import { ReactComponent as WarningTriangle } from '@sketch/icons/warning-triangle-64'
import { FormikProps } from 'formik'
import { initialValues } from './form'

const LOADING_PAGE_SIZE = 3
const ENTRIES_PATH = ['workspace', 'shares', 'entries']

// TODO: Review copy for NoResults, ErrorState and EmptyState
// https://linear.app/sketch/issue/SWEB-1480
const NoResults = () => (
  <Wrapper>
    <IconWrapper>
      <SearchIcon height="64px" />
    </IconWrapper>
    <Title>No search results</Title>
    <Description>
      No matching documents found. Try again with another term.
    </Description>
  </Wrapper>
)

const ErrorState = () => (
  <Wrapper>
    <IconWrapper>
      <WarningTriangle height="64px" />
    </IconWrapper>
    <Title>An error occurred</Title>
    <Description>
      An error occurred while getting the documents, please try again.
    </Description>
  </Wrapper>
)

const EmptyState = () => (
  <Wrapper>
    <IconWrapper>
      <StyledFolderClosed height="64px" />
    </IconWrapper>
    <Title>Project is empty</Title>
    <Description>
      Add some documents to the project, after which you can add them to your
      collection.
    </Description>
  </Wrapper>
)

export interface FormProps {
  selectedShares: string[]
  selectAll: boolean
}

interface SelectDocumentsStepProps {
  workspace: WorkspaceMinimalFragment
  formikBag: FormikProps<typeof initialValues>
}

export const SelectDocumentsStep = ({
  workspace,
  formikBag,
}: SelectDocumentsStepProps) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounceValue(search, 500)

  const {
    data,
    error,
    loading,
    fetchMore: fetchMoreShares,
  } = useGetSharesQuery({
    variables: {
      identifier: workspace.identifier,
      search: {
        name: debouncedSearch,
      },
    },
    fetchPolicy: 'cache-and-network',
  })
  const shares = data?.workspace.shares
  const sharesEntries = shares?.entries || []

  const after = shares?.meta.after
  const totalCount = shares?.meta.totalCount ?? 0
  const remainingCount = totalCount - sharesEntries.length
  const placeholderCount = Math.min(LOADING_PAGE_SIZE, remainingCount)

  const fetchMore = handleFetchMore(fetchMoreShares, ENTRIES_PATH, {
    dataIdFromObject,
    after,
  })

  const searchPlaceholder = `Search documents in "${workspace.name}"`

  const noResults = !sharesEntries.length && search
  const errorState = error ? <ErrorState /> : null
  const noResultsState = noResults ? <NoResults /> : null
  const emptyState = !loading && !sharesEntries.length ? <EmptyState /> : null

  const isSearchDisabled = !!(!loading && emptyState && !search)

  const resetSelectedShares = () => {
    formikBag.setFieldValue('shares', [])
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      formikBag.setFieldValue(
        'shares',
        sharesEntries.map(item => item.identifier)
      )
    }
    formikBag.handleChange(e)
  }

  return (
    <>
      <ContentContainer>
        <Title>Choose Documents</Title>
        <Description>
          Select the Documents, Libraries, and Templates you want to share with
          the world. These documents will be publicly visible, even if your
          profile is hidden.
        </Description>
      </ContentContainer>
      <Separator />
      <SelectableDocumentList
        // Data
        name="shares"
        items={sharesEntries}
        selectedItems={formikBag.values.shares}
        enableSelectAll
        selectAll={formikBag.values.selectAll}
        placeholderCount={placeholderCount}
        // Loading
        loading={loading}
        // Handlers
        onItemChange={handleChange}
        fetchMore={fetchMore}
        resetSelectedShares={resetSelectedShares}
        // Search
        search={search}
        searchPlaceholder={searchPlaceholder}
        onSearch={setSearch}
        isSearchDisabled={isSearchDisabled}
        // UI States
        emptyState={emptyState}
        errorState={errorState}
        noResultsState={noResultsState}
      />
    </>
  )
}
